import { AuctionStatus } from "../types/auctionTypes";

/**
 * Final/closed auction statuses where no more bidding is allowed
 */
const FINAL_STATUSES: readonly AuctionStatus[] = [
  AuctionStatus.Closed,
  AuctionStatus.Canceled,
  AuctionStatus.Completed,
  AuctionStatus.Pending,
  AuctionStatus.Won,
  AuctionStatus.Lost,
];

/**
 * Check if an auction status is in a final/closed state
 */
export const isFinalStatus = (status: number | AuctionStatus): boolean => {
  return FINAL_STATUSES.includes(status as AuctionStatus);
};

/**
 * Check if auction bidding time (endsAt) has passed
 */
const isAuctionEnded = (endsAt?: string): boolean => {
  if (!endsAt) return false;

  try {
    const now = new Date();
    const endTime = new Date(endsAt);
    return endTime.getTime() <= now.getTime();
  } catch (error) {
    console.error("Error parsing auction end time:", error);
    return false;
  }
};

export const getAuctionStatus = (
  statusId: number | AuctionStatus,
  endsAt?: string
) => {
  // If auction bidding time has passed and status is not already final, show as Closed
  if (endsAt && isAuctionEnded(endsAt)) {
    if (!isFinalStatus(statusId)) {
      return "Closed";
    }
  }

  switch (statusId) {
    case AuctionStatus.New:
      return "New";
    case AuctionStatus.EndingSoon:
      return "Ending Soon";
    case AuctionStatus.ExpiringToday:
      return "Expiring Today";
    case AuctionStatus.Closed:
      return "Closed";
    case AuctionStatus.Canceled:
      return "Canceled";
    case AuctionStatus.Completed:
      return "Completed";
    case AuctionStatus.Pending:
      return "Pending";
    case AuctionStatus.Won:
      return "Won";
    case AuctionStatus.Lost:
      return "Lost";
    default:
      return "Unknown";
  }
};

export const getStatusColor = (
  statusId: number | AuctionStatus,
  endsAt?: string
) => {
  // If auction bidding time has passed and status is not already final, show as Closed
  if (endsAt && isAuctionEnded(endsAt)) {
    if (!isFinalStatus(statusId)) {
      return "bg-gray-200 text-gray-600"; // Closed status color
    }
  }

  const statusColors: Record<number, string> = {
    [AuctionStatus.New]: "bg-blue-100 text-blue-800",
    [AuctionStatus.EndingSoon]: "bg-orange-100 text-orange-800",
    [AuctionStatus.ExpiringToday]: "bg-amber-100 text-amber-800",
    [AuctionStatus.Closed]: "bg-gray-200 text-gray-600",
    [AuctionStatus.Canceled]: "bg-rose-100 text-rose-700",
    [AuctionStatus.Completed]: "bg-emerald-100 text-emerald-700",
    [AuctionStatus.Pending]: "bg-purple-100 text-purple-700",
    [AuctionStatus.Won]: "bg-green-200 text-green-800",
    [AuctionStatus.Lost]: "bg-gray-100 text-gray-500",
  };

  return statusColors[statusId] || "bg-gray-100 text-gray-600";
};

/**
 * Check if bidding is allowed for a given auction status and auction end time
 */
export const canBid = (
  status: number | AuctionStatus,
  endsAt?: string
): boolean => {
  // Check if status allows bidding
  if (isFinalStatus(status)) {
    return false;
  }

  // If auction end time has passed, don't allow bidding
  if (endsAt && isAuctionEnded(endsAt)) {
    return false;
  }

  return true;
};

/**
 * Check if bid input should be available based on auction state and user role
 */
export const isBidInputAvailable = (params: {
  status: number | AuctionStatus;
  isOwner: boolean;
  isCompany: boolean;
  isSubscriptionActive: boolean;
  mode?: "default" | "preview";
  endsAt?: string;
}): boolean => {
  const { status, isOwner, isCompany, isSubscriptionActive, mode = "default", endsAt } = params;
  
  return (
    mode === "default" &&
    canBid(status, endsAt) &&
    !isOwner &&
    isCompany &&
    isSubscriptionActive
  );
};

